import { useContractDetailsContext, useUserLanguage } from '@/customHooks'
import { GetContractQuery } from '@/graphql/generated'
import { Col, Row } from 'react-grid-system'
import { Button, IncidentStatusOptionPill, PriorityPill, RowSpacer } from '../atoms'
import styled from 'styled-components'
import { useCallback, useMemo } from 'react'
import { incidentCategoryMapper, incidentTypeMapper } from '@/helpers'
import { useNavigate } from 'react-router'
import { pathURL } from '@/components/constants/urls'

type IncidentsForList = NonNullable<GetContractQuery['contract']['incidents']>[number]

const URL_TO_NAVIGATE_FOR_DETAILS = `/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`

const textsData = {
  en: {
    incidentsTable: {
    property: 'Property',
    unity: 'Unity',
    supplier: 'Supplier',
    type: 'Type',
    category: 'Category',
    priority: 'Priority',
    startDate: 'Start',
    endDate: 'End',
    subject: 'Subject',
    status: 'Status,'
    },
    noData: 'No incidents related to this contract',
    rowButton: 'Details',
  },
  es: {
    incidentsTable: {
      property: 'Propiedad',
      unity: 'Unity',
      supplier: 'Proveedor',
      type: 'Tipo',
      category: 'Categoría',
      priority: 'Prioridad',
      startDate: 'Inicio',
      endDate: 'Fin',
      subject: 'Asunto',
      status: 'Estado'
    },
    noData: 'No hay incidents relacionados a este contrato',
    rowButton: 'Detalles',
  },
}

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 160px;
`

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: solid 1px lightgray;
    background-color: ${({ theme }) => theme.colors.light};
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`
const readableDate = (date: Date) => {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const ContractDetailIncidentList = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()

  const { contract } = useContractDetailsContext()
  const incidents = contract?.incidents ?? []

  const dataFlatter = useCallback((data: IncidentsForList[] | undefined) => {
    if(!data) return []
    
    return data.map((element) => ({
      id: element.id,
      type: incidentTypeMapper(element.incidentTypeOption, userLanguage),
      category: incidentCategoryMapper(element.incidentCategoryOption, userLanguage),
      priority: element.priorityOption,
      endDate: readableDate(element.endDate),
      subject: element.subject,
      status: element.statusOption
    }))},
    [userLanguage],
  )
  
  const flattenedData = useMemo(
    () => dataFlatter(incidents),
    [dataFlatter],
  )

  const onDeatilsClickHandle = (id: string) => {
    navigate(URL_TO_NAVIGATE_FOR_DETAILS, { state: { id } })
  }

  if (incidents.length === 0) {
    return (
      <>
        <RowSpacer height={3} />
        <Row>
          <Col>
            <span><b>{texts.noData}</b></span>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <StyledGridTable>
      <StyledGridHeader>
        <div>{texts.incidentsTable.subject}</div>
        <div>{texts.incidentsTable.endDate}</div>
        <div>{texts.incidentsTable.type}</div>
        <div>{texts.incidentsTable.category}</div>
        <div>{texts.incidentsTable.priority}</div>
        <div>{texts.incidentsTable.status}</div>
        <div></div>
      </StyledGridHeader>
      {flattenedData?.map((incident) => (
        <StyledGridRow key={incident.id}>
          <div>{incident.subject}</div>
          <div>{incident.endDate}</div>
          <div>{incident.type}</div>
          <div>{incident.category}</div>
          <div><PriorityPill option={incident.priority} /></div>
          <div><IncidentStatusOptionPill option={incident.status} /></div>
          <div>
            <Button
              secondary
              upperCased
              onClick={() => onDeatilsClickHandle(incident.id)}
            >
              {texts.rowButton}
            </Button>
          </div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  )
}